import {
  Lock,
  Globe,
  ArrowRight,
  ShieldCheck,
  Github,
} from 'lucide-react';

import {
  Badge,
  Box,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Loader,
} from '@mantine/core';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';

import useRepos from "./repoProvider";
import useUser from "./userProvider";
import useConfig from "./configProvider";
import ErrorScreen from "./ErrorScreen.jsx";

export default function RepoSelector() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const {userInfo} = useUser();
  const config = useConfig();
  const githubAppSlug = config?.data?.githubAppSlug;
  const {data: repos, error, mutate} = useRepos(userInfo?.sub);

  if ((userInfo == null) && (error?.status === 403)) {
    const url = new URL("/api/login", document.location);
    const params = new URLSearchParams({cont: document.location});
    url.search = params.toString();
    document.location = url.toString();
    return null;
  }

  if (error) {
    return (
      <ErrorScreen
        status={error.status}
        title={t('Repositories unavailable')}
        message={t('We could not load your repositories at the moment.')}
        onRetry={() => mutate()}
      />
    );
  }

  if (!repos) {
    return (
      <Box mih="40vh">
        <Container fluid px="md">
          <Stack align="center" justify="center" py={40}>
            <Loader />
            <Text c="dimmed">{t('Loading repositories...')}</Text>
          </Stack>
        </Container>
      </Box>
    );
  }

  // Handle both old (array) and new ({repos, installed}) response formats
  const repoList = Array.isArray(repos) ? repos : (repos?.repos || []);
  const isInstalled = Array.isArray(repos) ? true : repos?.installed;

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        <Stack gap="xl">
          <Stack gap={4}>
            <Title order={1}>{t('Your Repositories')}</Title>
            <Text c="dimmed" size="sm" maw={720}>
              {t('Select a repository to view its timestamp verification status and proof history.')}
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {repoList.map((repo) => (
              <Paper
                key={repo.name}
                withBorder
                radius="md"
                p="md"
                style={{ cursor: 'pointer' }}
                onClick={() => setLocation(`/status/${userInfo.sub}/${repo.name}`)}
              >
                <Group justify="space-between" wrap="wrap">
                  <Stack gap={4}>
                    <Group gap="xs">
                      <ThemeIcon
                        variant="light"
                        color={repo?.private ? 'orange' : 'teal'}
                        radius="xl"
                        size="sm"
                      >
                        {repo?.private ? <Lock size={12} /> : <Globe size={12} />}
                      </ThemeIcon>
                      <Text fw={600}>{userInfo.sub}/{repo.name}</Text>
                    </Group>
                    <Group gap="xs">
                      {repo?.private !== null && (
                        <Badge
                          variant="light"
                          color={repo?.private ? 'orange' : 'teal'}
                          size="xs"
                        >
                          {repo?.private ? t('Private') : t('Public')}
                        </Badge>
                      )}
                      {repo?.commits > 0 && (
                        <Badge variant="light" color="blue" size="xs">
                          {repo.commits} {t('Proofs')}
                        </Badge>
                      )}
                      {repo?.stamp && (
                        <Text size="xs" c="dimmed">
                          {t('Last')}: {repo.stamp}
                        </Text>
                      )}
                    </Group>
                  </Stack>
                  <ArrowRight size={16} />
                </Group>
              </Paper>
            ))}
          </SimpleGrid>

          {repoList.length === 0 && (
            <Paper withBorder radius="md" p="xl">
              {isInstalled ? (
                <Stack align="center" gap="md">
                  <ShieldCheck size={48} />
                  <Text fw={600} size="lg">{t('No repositories selected')}</Text>
                  <Text c="dimmed" size="sm" ta="center" maw={400}>
                    {t('Go to your GitHub App settings to grant access to repositories.')}
                  </Text>
                  {githubAppSlug ? (
                    <Button
                      component="a"
                      href={`https://github.com/settings/installations`}
                      target="_blank"
                      leftSection={<Github size={16} />}
                    >
                      {t('GitHub App Settings')}
                    </Button>
                  ) : null}
                </Stack>
              ) : (
                <Stack align="center" gap="md">
                  <ShieldCheck size={48} />
                  <Text fw={600} size="lg">{t('No repositories found')}</Text>
                  <Text c="dimmed" size="sm" ta="center" maw={400}>
                    {t('Install the Timestamp GIT GitHub App on your repositories to get started.')}
                  </Text>
                  {githubAppSlug ? (
                    <Button
                      component="a"
                      href={`https://github.com/apps/${githubAppSlug}/installations/new`}
                      target="_blank"
                      leftSection={<Github size={16} />}
                    >
                      {t('Install GitHub App')}
                    </Button>
                  ) : null}
                </Stack>
              )}
            </Paper>
          )}
        </Stack>
      </Container>
    </Box>
  );
}